import * as React from 'react';
import { useState } from 'react';
import useModerationToken from '@/src/GalleryConfiguration/components/hooks/useModerationToken';
import { ConfirmDialog, SectionCard } from '../../../components/forms';
import { LinkRow, SectionStatus } from './parts';

/* Moderation: the magic link (public write power) and the intake hold.
   Each saves on its own, like v2. */

const MODES = [
    {
        hold: false,
        title: 'New photos appear immediately',
        sub: 'Every capture and upload goes straight to the gallery, slideshow and sharing. Whoever holds the moderation link can hide photos afterwards.',
    },
    {
        hold: true,
        title: 'Hold new photos for approval',
        sub: 'Nothing shows publicly until someone approves it from the moderation link. Applies to every source — booth, uploads, texts and web.',
    },
] as const;

export default function ModerationSection({
    galleryId,
    galleryUrl,
    slug,
    token,
    hold,
    onToken,
    onHold,
    copied,
    onCopy,
}: {
    galleryId: number;
    galleryUrl: string;
    slug: string;
    token: string | null;
    hold: boolean;
    onToken: (t: string | null) => void;
    onHold: (h: boolean) => void;
    copied: boolean;
    onCopy: (url: string) => void;
}) {
    const { createModerationToken, revokeModerationToken, setModerationHold } = useModerationToken();
    const [busy, setBusy] = useState(false);
    const [linkError, setLinkError] = useState<string | null>(null);
    const [confirm, setConfirm] = useState<'create' | 'revoke' | null>(null);
    const [holdState, setHoldState] = useState<'idle' | 'saving' | 'failed' | 'saved'>('idle');

    const url = slug && token ? `${galleryUrl}/moderation/${slug}/${token}` : '';

    const create = async () => {
        if (busy) return;
        setBusy(true);
        setLinkError(null);
        const row = await createModerationToken(galleryId);
        setBusy(false);
        if (row?.token) onToken(row.token);
        else setLinkError('Couldn’t create the link — try again.');
    };

    const revoke = async () => {
        if (!token || busy) return;
        setBusy(true);
        setLinkError(null);
        const ok = await revokeModerationToken(token);
        setBusy(false);
        if (ok) onToken(null);
        else setLinkError('Couldn’t revoke the link — it still works. Try again.');
    };

    const setMode = async (next: boolean) => {
        if (next === hold || holdState === 'saving') return;
        setHoldState('saving');
        const ok = await setModerationHold(galleryId, next);
        if (ok) {
            onHold(next);
            setHoldState('saved');
        } else {
            setHoldState('failed');
        }
    };

    return (
        <SectionCard title="Moderation" note="saves immediately" action={<SectionStatus state={holdState} />}>
            {url ? (
                <LinkRow
                    title="Moderation link"
                    href={url}
                    note="Anyone with this link can approve or hide photos — no login."
                    copied={copied}
                    onCopy={() => onCopy(url)}
                    extra={
                        <button type="button" className="tb-btn sm gl-danger" disabled={busy} onClick={() => setConfirm('revoke')}>
                            {busy ? 'Revoking…' : 'Revoke'}
                        </button>
                    }
                />
            ) : (
                <div className="trow2 gl-link">
                    <span className="gl-link-t">
                        <span className="nm2">Moderation link</span>
                        <span className="sub2">
                            {slug ? 'A magic link to approve or hide photos without logging in.' : 'Set a public address first.'}
                        </span>
                    </span>
                    <span className="gl-link-a">
                        <button type="button" className="tb-btn sm" disabled={busy || !slug} onClick={() => setConfirm('create')}>
                            {busy ? 'Creating…' : 'Create link'}
                        </button>
                    </span>
                </div>
            )}
            {linkError && (
                <p className="field-e" role="alert">
                    {linkError}
                </p>
            )}

            <fieldset className="gl-radios" disabled={holdState === 'saving'}>
                <legend className="field-l">New photos</legend>
                {MODES.map((m) => (
                    <label key={m.title} className={hold === m.hold ? 'gl-radio on' : 'gl-radio'}>
                        <input
                            type="radio"
                            name={`gl-hold-${galleryId}`}
                            checked={hold === m.hold}
                            onChange={() => setMode(m.hold)}
                        />
                        <span>
                            <span className="lbl">{m.title}</span>
                            <span className="sub3">{m.sub}</span>
                        </span>
                    </label>
                ))}
            </fieldset>
            {hold && (
                <p className="gl-note">
                    <span className="gl-dot" aria-hidden="true" />
                    New photos are held — {url ? 'share the moderation link with whoever approves.' : 'create a moderation link so someone can approve them.'}
                </p>
            )}

            <ConfirmDialog
                open={confirm === 'create'}
                danger={false}
                title="Create moderation link?"
                body="Anyone with this link can approve or hide photos in this gallery, without logging in. Share it only with people you trust; you can revoke it any time."
                actionLabel="Create link"
                onConfirm={create}
                onClose={() => setConfirm(null)}
            />
            <ConfirmDialog
                open={confirm === 'revoke'}
                title="Revoke moderation link?"
                body="The current link stops working immediately for everyone who has it. You can create a new link afterwards."
                actionLabel="Revoke link"
                onConfirm={revoke}
                onClose={() => setConfirm(null)}
            />
        </SectionCard>
    );
}
